(() => {
  'use strict';

  if (window.ZoeDIYOOSQ1) {
    return;
  }

  const OPTION_KEY = 'zoediy_oos_option';
  const VERSION_KEY = 'zoediy_oos_policy_version';
  const LINE_OPTION_KEY = '_zoediy_oos_option';
  const LINE_VERSION_KEY = '_zoediy_oos_policy_version';
  const VERSION = 'Q1';
  const POLICY_ENABLED = window.ZoeDIYOOSPolicyEnabled !== false;
  const DEFAULT_OPTION = 'B_REFUND';
  const VALID_OPTIONS = new Set(['A_SUBSTITUTE', 'B_REFUND', 'C_CONTACT_FIRST']);
  const STATUS_TEXT = {
    idle: '',
    saving: 'Saving…',
    saved: 'Selection saved',
    preparing: 'Preparing your checkout…',
    error: '',
    invalid: ''
  };

  let lastCart = null;
  let attributeQueue = Promise.resolve();
  let latestAttributeTask = null;
  let latestRequestedOption = null;
  let localChoiceActive = false;
  let uiStatus = { type: 'idle', message: '', recovery: false, source: '' };

  let mirrorTimer = null;
  let mirrorTarget = null;
  let mirrorSeedCart = null;
  let mirrorDirty = false;
  let mirrorRunner = null;

  const cartUrl = () => window.themeVariables?.routes?.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
  const cartChangeUrl = () => window.themeVariables?.routes?.cartChangeUrl || `${cartUrl()}/change`;

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const description = data?.description || data?.message || `Request failed with status ${response.status}`;
      throw new Error(description);
    }

    if (!data) {
      throw new Error('Shopify returned an empty cart response.');
    }

    return data;
  }

  function fetchCart() {
    return requestJSON(`${cartUrl()}.js`, { cache: 'no-store' });
  }

  function updateCartAttributes(option) {
    return requestJSON(`${cartUrl()}/update.js`, {
      method: 'POST',
      body: JSON.stringify({
        attributes: {
          [OPTION_KEY]: option || '',
          [VERSION_KEY]: option ? VERSION : ''
        }
      })
    });
  }

  function getState(cart) {
    const attributes = cart?.attributes || {};
    const option = String(attributes[OPTION_KEY] || '').trim();
    const version = String(attributes[VERSION_KEY] || '').trim();

    if (!option && !version) {
      return { type: 'empty', option: '', version: '' };
    }

    if (VALID_OPTIONS.has(option) && version === VERSION) {
      return { type: 'valid', option, version };
    }

    return { type: 'invalid', option, version };
  }

  function getProperties(item) {
    return item && item.properties && typeof item.properties === 'object' ? { ...item.properties } : {};
  }

  function mirrorNeedsUpdate(item, targetOption) {
    const properties = getProperties(item);
    const currentOption = String(properties[LINE_OPTION_KEY] || '').trim();
    const currentVersion = String(properties[LINE_VERSION_KEY] || '').trim();

    if (targetOption) {
      return currentOption !== targetOption || currentVersion !== VERSION;
    }

    return Boolean(currentOption || currentVersion);
  }

  function createDesiredProperties(item, targetOption) {
    const properties = getProperties(item);

    if (targetOption) {
      properties[LINE_OPTION_KEY] = targetOption;
      properties[LINE_VERSION_KEY] = VERSION;
      return properties;
    }

    const hadOptionProperty = Object.prototype.hasOwnProperty.call(properties, LINE_OPTION_KEY);
    const hadVersionProperty = Object.prototype.hasOwnProperty.call(properties, LINE_VERSION_KEY);
    delete properties[LINE_OPTION_KEY];
    delete properties[LINE_VERSION_KEY];

    if (Object.keys(properties).length === 0 && (hadOptionProperty || hadVersionProperty)) {
      properties[LINE_OPTION_KEY] = '';
      properties[LINE_VERSION_KEY] = '';
    }

    return properties;
  }

  async function synchronizeLineMirrors(targetOption, startingCart = null) {
    let cart = startingCart || await fetchCart();
    const maximumAttempts = Math.max(20, (cart.items?.length || 0) * 5 + 10);

    for (let attempt = 0; attempt < maximumAttempts; attempt += 1) {
      const items = Array.isArray(cart.items) ? cart.items : [];
      let lineIndex = -1;

      for (let index = items.length - 1; index >= 0; index -= 1) {
        if (mirrorNeedsUpdate(items[index], targetOption)) {
          lineIndex = index;
          break;
        }
      }

      if (lineIndex === -1) {
        return cart;
      }

      const item = items[lineIndex];
      cart = await requestJSON(`${cartChangeUrl()}.js`, {
        method: 'POST',
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: item.quantity,
          properties: createDesiredProperties(item, targetOption)
        })
      });
    }

    throw new Error('The cart line-item mirror could not be synchronized completely.');
  }

  function verifyState(cart, targetOption) {
    const state = getState(cart);

    if (targetOption) {
      if (state.type !== 'valid' || state.option !== targetOption) {
        return false;
      }
    } else if (state.type !== 'empty') {
      return false;
    }

    return (cart.items || []).every((item) => !mirrorNeedsUpdate(item, targetOption));
  }

  function setProductFields(option) {
    document.querySelectorAll('[data-zoediy-oos-product-option]').forEach((input) => {
      input.value = option || '';
    });

    document.querySelectorAll('[data-zoediy-oos-product-version]').forEach((input) => {
      input.value = option ? VERSION : '';
    });
  }

  function cleanupCartWishlistArtifacts() {
    document.querySelectorAll([
      '#mini-cart .product-item__wishlist-wrapper',
      '#mini-cart .wishlist-engine',
      '#mini-cart [class*="wishlist"]',
      '#mini-cart [class*="Wishlist"]',
      '#mini-cart [class*="swym"]',
      '#mini-cart [class*="Swym"]',
      '#mini-cart svg.wishlist-icon',
      'cart-drawer .product-item__wishlist-wrapper',
      'cart-drawer .wishlist-engine',
      'cart-drawer [class*="wishlist"]',
      'cart-drawer [class*="Wishlist"]',
      'cart-drawer [class*="swym"]',
      'cart-drawer [class*="Swym"]',
      'cart-drawer svg.wishlist-icon',
      '.mini-cart .product-item__wishlist-wrapper',
      '.mini-cart .wishlist-engine',
      '.mini-cart [class*="wishlist"]',
      '.mini-cart [class*="Wishlist"]',
      '.mini-cart [class*="swym"]',
      '.mini-cart [class*="Swym"]',
      '.mini-cart svg.wishlist-icon'
    ].join(',')).forEach((node) => {
      node.remove();
    });
  }

  function ensureCartAttributeInput(form, name) {
    let input = form.querySelector(`input[data-zoediy-oos-cart-attribute][name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.zoediyOosCartAttribute = 'true';
      form.appendChild(input);
    }

    return input;
  }

  function ensureCheckoutLabel(button) {
    let label = button.querySelector('[data-zoediy-oos-checkout-label]');
    if (label) {
      return label;
    }

    label = document.createElement('span');
    label.dataset.zoediyOosCheckoutLabel = 'true';

    const separator = button.querySelector('.square-separator');
    const childNodes = Array.from(button.childNodes);
    const separatorIndex = separator ? childNodes.indexOf(separator) : childNodes.length;
    const textNode = childNodes.find((node, index) => (
      node.nodeType === Node.TEXT_NODE
      && index < separatorIndex
      && node.textContent.trim() !== ''
    ));

    if (textNode) {
      textNode.replaceWith(label);
    } else if (separator) {
      button.insertBefore(label, separator);
    } else {
      button.appendChild(label);
    }

    return label;
  }

  function syncCheckoutControls(option) {
    const normalized = VALID_OPTIONS.has(option) ? option : null;

    document.querySelectorAll('.cart__recap, #mini-cart-form').forEach((form) => {
      const optionInput = ensureCartAttributeInput(form, `attributes[${OPTION_KEY}]`);
      const versionInput = ensureCartAttributeInput(form, `attributes[${VERSION_KEY}]`);
      optionInput.value = normalized || '';
      versionInput.value = normalized ? VERSION : '';
    });

    const buttonLabel = normalized ? 'Checkout' : 'Checkout — Default Option B';
    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      ensureCheckoutLabel(button).textContent = buttonLabel;
      button.dataset.zoediyOosOption = normalized || '';
    });
  }

  function applyOptimisticChoice(option) {
    document.querySelectorAll('[data-zoediy-oos-selector]').forEach((root) => {
      if (root.dataset.mode === 'buynow') {
        return;
      }

      root.querySelectorAll('[data-zoediy-oos-radio]').forEach((radio) => {
        radio.checked = Boolean(option) && radio.value === option;
      });

      const clearButton = root.querySelector('[data-zoediy-oos-clear]');
      if (clearButton) {
        clearButton.disabled = !option;
        clearButton.setAttribute('aria-disabled', option ? 'false' : 'true');
      }

      root.dataset.cartState = option ? 'valid' : 'empty';
    });

    setProductFields(option);
    syncCheckoutControls(option);
  }

  function setUIStatus(type, message = STATUS_TEXT[type] || '', recovery = false, source = '') {
    uiStatus = { type, message, recovery, source };
  }

  function applyUIStatus() {
    return;
  }

  function renderCartState(cart) {
    if (!cart) {
      return;
    }

    lastCart = cart;
    const state = getState(cart);

    if (localChoiceActive) {
      applyOptimisticChoice(latestRequestedOption);
      return;
    }

    const option = state.type === 'valid' ? state.option : null;
    latestRequestedOption = option;
    applyOptimisticChoice(option);
  }

  function scheduleMirror(targetOption, seedCart = null, delay = 0) {
    mirrorTarget = targetOption || null;
    mirrorSeedCart = seedCart || lastCart;
    mirrorDirty = true;

    if (mirrorTimer) {
      window.clearTimeout(mirrorTimer);
    }

    mirrorTimer = window.setTimeout(() => {
      mirrorTimer = null;
      runMirrorLoop().catch((error) => {
        console.error('[ZoeDIY OOS Q1] Background mirror failed:', error);
      });
    }, delay);
  }

  function runMirrorLoop() {
    if (mirrorRunner) {
      return mirrorRunner;
    }

    mirrorRunner = (async () => {
      while (mirrorDirty) {
        mirrorDirty = false;
        const target = mirrorTarget;
        const seed = mirrorSeedCart;
        mirrorSeedCart = null;

        let cart = seed || lastCart || await fetchCart();
        cart = await synchronizeLineMirrors(target, cart);
        lastCart = cart;

        if (target !== mirrorTarget) {
          mirrorDirty = true;
        }
      }

      return lastCart;
    })().finally(() => {
      mirrorRunner = null;
    });

    return mirrorRunner;
  }

  async function flushMirror() {
    if (latestAttributeTask) {
      await latestAttributeTask;
    }

    if (mirrorTimer) {
      window.clearTimeout(mirrorTimer);
      mirrorTimer = null;
    }

    if (mirrorDirty) {
      await runMirrorLoop();
    } else if (mirrorRunner) {
      await mirrorRunner;
    }

    const target = latestRequestedOption || null;
    const cart = lastCart || await fetchCart();

    if (!verifyState(cart, target)) {
      mirrorTarget = target;
      mirrorSeedCart = cart;
      mirrorDirty = true;
      await runMirrorLoop();
    }

    if (!verifyState(lastCart, target)) {
      throw new Error('The saved cart state did not pass checkout verification.');
    }

    return lastCart;
  }

  function saveChoice(targetOption, { silent = false } = {}) {
    const normalizedTarget = VALID_OPTIONS.has(targetOption) ? targetOption : null;
    latestRequestedOption = normalizedTarget;
    localChoiceActive = true;
    applyOptimisticChoice(normalizedTarget);

    const task = attributeQueue.then(async () => {
      const cart = await updateCartAttributes(normalizedTarget);
      lastCart = cart;

      if (latestRequestedOption === normalizedTarget) {
        scheduleMirror(normalizedTarget, cart);
        localChoiceActive = false;
        renderCartState(cart);
      }

      return cart;
    }).catch(async (error) => {
      console.error('[ZoeDIY OOS Q1] Attribute save failed:', error);

      if (latestRequestedOption === normalizedTarget) {
        localChoiceActive = true;
        applyOptimisticChoice(normalizedTarget);
      }

      throw error;
    });

    attributeQueue = task.catch(() => undefined);
    latestAttributeTask = task;
    task.finally(() => {
      if (latestAttributeTask === task) {
        latestAttributeTask = null;
      }
    }).catch(() => undefined);

    return task;
  }



  function normalizeRootPath() {
    const root = window.Shopify?.routes?.root || '/';
    const withLeadingSlash = root.startsWith('/') ? root : `/${root}`;
    return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
  }

  function encodeBase64Url(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    const chunkSize = 8192;

    for (let index = 0; index < bytes.length; index += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
    }

    return window.btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
  }


  function formatBuyNowMoney(cents) {
    const numericCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    const formatString = window.themeVariables?.settings?.moneyWithCurrencyFormat
      || window.themeVariables?.settings?.moneyFormat
      || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      const normalized = Number(number);
      if (!Number.isFinite(normalized)) {
        return '0';
      }

      const parts = (normalized / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return whole + (parts[1] ? `${decimal}${parts[1]}` : '');
    }

    if (!match) {
      const currency = window.Shopify?.currency?.active || '';
      return `${formatWithDelimiters(numericCents)} ${currency}`.trim();
    }

    let value = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(numericCents, 0);
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(numericCents, 2, ' ', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(numericCents, 2, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(numericCents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(numericCents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(numericCents, 0, ' ', '.');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = formatWithDelimiters(numericCents, 0, "'", '.');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(numericCents, 2);
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function moneyMarkupToPlainText(value) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(String(value || ''), 'text/html');
    return String(documentFragment.body?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getBuyNowQuantity(form) {
    const quantityInput = form?.querySelector('[name="quantity"]');
    const requestedQuantity = Number.parseInt(String(quantityInput?.value || '1'), 10);
    return Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
  }

  function updateBuyNowButton(modal, loadingLabel = '') {
    if (!modal) {
      return;
    }

    const button = modal.querySelector('[data-zoediy-oos-buy-now-continue]');
    const label = button?.querySelector('[data-zoediy-oos-buy-now-label]');
    const total = button?.querySelector('[data-zoediy-oos-buy-now-total]');
    const form = document.getElementById(modal.dataset.formId || '');
    if (!button || !label || !total) {
      return;
    }

    const selectedOption = VALID_OPTIONS.has(modal.dataset.selectedOption) ? modal.dataset.selectedOption : null;
    const unitPrice = Number.parseInt(String(modal.dataset.unitPrice || '0'), 10) || 0;
    const totalPrice = unitPrice * getBuyNowQuantity(form);
    const formattedTotal = moneyMarkupToPlainText(formatBuyNowMoney(totalPrice));
    const buttonLabel = loadingLabel || (selectedOption ? 'Buy it now' : 'Buy it now — Default Option B');

    label.textContent = buttonLabel;
    total.textContent = formattedTotal;
    button.setAttribute('aria-label', `${buttonLabel}, ${formattedTotal}`);
  }

  function setBuyNowChoice(selector, option) {
    const normalized = VALID_OPTIONS.has(option) ? option : null;
    const modal = selector.closest('[data-zoediy-oos-buy-now-modal]');

    selector.querySelectorAll('[data-zoediy-oos-radio]').forEach((radio) => {
      radio.checked = Boolean(normalized) && radio.value === normalized;
    });

    const clearButton = selector.querySelector('[data-zoediy-oos-clear]');
    if (clearButton) {
      clearButton.disabled = !normalized;
      clearButton.setAttribute('aria-disabled', normalized ? 'false' : 'true');
    }

    selector.dataset.cartState = normalized ? 'valid' : 'empty';

    if (modal) {
      modal.dataset.selectedOption = normalized || '';
      const continueButton = modal.querySelector('[data-zoediy-oos-buy-now-continue]');
      const status = modal.querySelector('[data-zoediy-oos-buy-now-status]');

      if (continueButton && !continueButton.dataset.loading) {
        updateBuyNowButton(modal);
      }

      if (status && !continueButton?.dataset.loading) {
        status.textContent = '';
      }
    }
  }

  function getModalByFormId(formId) {
    return Array.from(document.querySelectorAll('[data-zoediy-oos-buy-now-modal]'))
      .find((modal) => modal.dataset.formId === formId) || null;
  }

  function getFocusableElements(modal) {
    return Array.from(modal.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
  }

  function openBuyNowModal(trigger) {
    const modal = getModalByFormId(trigger.dataset.formId || '');
    if (!modal || trigger.disabled) {
      return;
    }

    modal._zoediyReturnFocus = trigger;
    modal.hidden = false;
    document.documentElement.classList.add('zoediy-oos-modal-open');
    document.body.classList.add('zoediy-oos-modal-open');

    const selector = modal.querySelector('[data-zoediy-oos-selector]');
    if (selector) {
      const checked = selector.querySelector('[data-zoediy-oos-radio]:checked');
      setBuyNowChoice(selector, checked?.value || null);
    }

    window.requestAnimationFrame(() => {
      modal.querySelector('.zoediy-oos-buynow-modal__close')?.focus();
    });
  }

  function closeBuyNowModal(modal) {
    if (!modal || modal.hidden) {
      return;
    }

    modal.hidden = true;
    document.documentElement.classList.remove('zoediy-oos-modal-open');
    document.body.classList.remove('zoediy-oos-modal-open');
    modal._zoediyReturnFocus?.focus?.();
  }

  function collectBuyNowProperties(form, selectedOption) {
    const data = new FormData(form);
    const properties = {};

    for (const [name, rawValue] of data.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (!match) {
        continue;
      }

      const key = match[1];
      if (key === LINE_OPTION_KEY || key === LINE_VERSION_KEY) {
        continue;
      }

      const value = typeof rawValue === 'string' ? rawValue : rawValue?.name || '';
      if (value !== '') {
        properties[key] = value;
      }
    }

    if (selectedOption) {
      properties[LINE_OPTION_KEY] = selectedOption;
      properties[LINE_VERSION_KEY] = VERSION;
    }

    return properties;
  }

  function buildBuyNowPermalink(form, selectedOption) {
    const idInput = form.querySelector('[name="id"]');
    const variantId = String(idInput?.value || '').trim();
    if (!/^\d+$/.test(variantId)) {
      throw new Error('Please select an available product option.');
    }

    const data = new FormData(form);
    const sellingPlan = String(data.get('selling_plan') || '').trim();
    if (sellingPlan) {
      throw new Error('Buy it now is not available for this purchase option. Please use Add to cart.');
    }

    const requestedQuantity = Number.parseInt(String(data.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
    const url = new URL(`${normalizeRootPath()}cart/${variantId}:${quantity}`, window.location.origin);
    const properties = collectBuyNowProperties(form, selectedOption);

    if (Object.keys(properties).length > 0) {
      url.searchParams.set('properties', encodeBase64Url(JSON.stringify(properties)));
    }

    if (selectedOption) {
      url.searchParams.set(`attributes[${OPTION_KEY}]`, selectedOption);
      url.searchParams.set(`attributes[${VERSION_KEY}]`, VERSION);
    }

    return url.toString();
  }

  async function continueBuyNow(modal) {
    const continueButton = modal.querySelector('[data-zoediy-oos-buy-now-continue]');
    const status = modal.querySelector('[data-zoediy-oos-buy-now-status]');
    const form = document.getElementById(modal.dataset.formId || '');

    if (!continueButton || !form) {
      return;
    }

    const selectedOption = VALID_OPTIONS.has(modal.dataset.selectedOption) ? modal.dataset.selectedOption : null;

    if (!form.reportValidity()) {
      if (status) {
        status.textContent = 'Please complete the required product information before checkout.';
      }
      return;
    }

    try {
      continueButton.disabled = true;
      continueButton.dataset.loading = 'true';
      continueButton.setAttribute('aria-busy', 'true');
      updateBuyNowButton(modal, 'Opening secure checkout…');
      if (status) {
        status.textContent = selectedOption ? 'Attaching your selection and opening checkout…' : 'Opening checkout with default Option B…';
      }

      const checkoutUrl = buildBuyNowPermalink(form, selectedOption);
      window.location.assign(checkoutUrl);
    } catch (error) {
      continueButton.disabled = false;
      delete continueButton.dataset.loading;
      continueButton.removeAttribute('aria-busy');
      updateBuyNowButton(modal);
      if (status) {
        status.textContent = error?.message || 'Checkout could not be opened. Please try again.';
      }
    }
  }

  function initializeBuyNowModals() {
    document.querySelectorAll('[data-zoediy-oos-buy-now-modal]').forEach((modal) => {
      if (modal.dataset.initialized === 'true') {
        return;
      }

      modal.dataset.initialized = 'true';
      const selector = modal.querySelector('[data-zoediy-oos-selector]');
      const checked = selector?.querySelector('[data-zoediy-oos-radio]:checked');
      if (selector) {
        setBuyNowChoice(selector, checked?.value || null);
      }

      const form = document.getElementById(modal.dataset.formId || '');
      const trigger = document.querySelector(`[data-zoediy-oos-buy-now-open][data-form-id="${modal.dataset.formId}"]`);
      form?.addEventListener('variant:changed', (event) => {
        const variant = event.detail?.variant;
        const available = Boolean(variant?.available);
        if (Number.isFinite(Number(variant?.price))) {
          modal.dataset.unitPrice = String(variant.price);
        }
        updateBuyNowButton(modal);
        if (trigger) {
          trigger.disabled = !available;
        }
        if (!available && !modal.hidden) {
          closeBuyNowModal(modal);
        }
      });

      const handleQuantityChange = (event) => {
        if (event.target?.matches?.('[name="quantity"]')) {
          updateBuyNowButton(modal);
        }
      };
      form?.addEventListener('input', handleQuantityChange);
      form?.addEventListener('change', handleQuantityChange);
      updateBuyNowButton(modal);
    });
  }

  async function initialize() {
    initializeBuyNowModals();
    cleanupCartWishlistArtifacts();

    try {
      const cart = await fetchCart();
      const state = getState(cart);
      latestRequestedOption = state.type === 'valid' ? state.option : null;
      renderCartState(cart);
      cleanupCartWishlistArtifacts();
      window.setTimeout(cleanupCartWishlistArtifacts, 250);

    } catch (error) {
      console.error('[ZoeDIY OOS Q1] Initial cart load failed:', error);
      setUIStatus('idle', '', false, '');
    }
  }

  async function pausePolicyAndClearLegacyState() {
    try {
      let cart = await updateCartAttributes(null);
      cart = await synchronizeLineMirrors(null, cart);
      lastCart = cart;
    } catch (error) {
      console.error('[ZoeDIY OOS Q1] Paused-policy cleanup failed:', error);
    }
  }

  if (!POLICY_ENABLED) {
    window.ZoeDIYOOSQ1 = {
      version: VERSION,
      paused: true,
      refresh: () => undefined,
      getCart: () => lastCart
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', pausePolicyAndClearLegacyState, { once: true });
    } else {
      pausePolicyAndClearLegacyState();
    }
    return;
  }

  document.addEventListener('change', (event) => {
    const radio = event.target.closest('[data-zoediy-oos-radio]');
    if (!radio || !VALID_OPTIONS.has(radio.value)) {
      return;
    }

    const selector = radio.closest('[data-zoediy-oos-selector]');
    if (selector?.dataset.mode === 'buynow') {
      setBuyNowChoice(selector, radio.value);
      return;
    }

    saveChoice(radio.value).catch(() => undefined);
  });

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-zoediy-oos-buy-now-open]');
    if (openButton) {
      event.preventDefault();
      openBuyNowModal(openButton);
      return;
    }

    const closeButton = event.target.closest('[data-zoediy-oos-buy-now-close]');
    if (closeButton) {
      event.preventDefault();
      closeBuyNowModal(closeButton.closest('[data-zoediy-oos-buy-now-modal]'));
      return;
    }

    const buyNowContinue = event.target.closest('[data-zoediy-oos-buy-now-continue]');
    if (buyNowContinue) {
      event.preventDefault();
      continueBuyNow(buyNowContinue.closest('[data-zoediy-oos-buy-now-modal]'));
      return;
    }

    const clearButton = event.target.closest('[data-zoediy-oos-clear]');
    if (clearButton) {
      event.preventDefault();
      if (clearButton.disabled) {
        return;
      }

      const selector = clearButton.closest('[data-zoediy-oos-selector]');
      if (selector?.dataset.mode === 'buynow') {
        setBuyNowChoice(selector, null);
        return;
      }

      saveChoice(null).catch(() => undefined);
      return;
    }

  });

  document.addEventListener('keydown', (event) => {
    const modal = document.querySelector('[data-zoediy-oos-buy-now-modal]:not([hidden])');
    if (!modal) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeBuyNowModal(modal);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = getFocusableElements(modal);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    const form = event.target;
    const isKnownCartCheckoutForm = form?.matches?.('.cart__recap, #mini-cart-form') && form.querySelector('[name="checkout"]');
    const isCheckout = submitter?.name === 'checkout' || submitter?.matches?.('[name="checkout"]') || isKnownCartCheckoutForm;

    if (!isCheckout) {
      return;
    }

    if (form.dataset.zoediyOosMirrorReady === 'true') {
      delete form.dataset.zoediyOosMirrorReady;
      syncCheckoutControls(latestRequestedOption);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    syncCheckoutControls(latestRequestedOption);
    flushMirror().then(() => {
      form.dataset.zoediyOosMirrorReady = 'true';
      if (typeof form.requestSubmit === 'function' && submitter instanceof HTMLElement) {
        form.requestSubmit(submitter);
      } else {
        HTMLFormElement.prototype.submit.call(form);
      }
    }).catch((error) => {
      console.error('[ZoeDIY OOS Q1] Checkout mirror failed:', error);
      form.dataset.zoediyOosMirrorReady = 'true';
      HTMLFormElement.prototype.submit.call(form);
    });
  }, true);

  const handleCartEvent = (event) => {
    const cart = event.detail?.cart;
    if (!cart) {
      return;
    }

    window.requestAnimationFrame(() => {
      renderCartState(cart);
      cleanupCartWishlistArtifacts();
      window.setTimeout(cleanupCartWishlistArtifacts, 250);
    });
  };

  document.documentElement.addEventListener('cart:updated', handleCartEvent);
  document.documentElement.addEventListener('cart:refresh', handleCartEvent);
  document.addEventListener('shopify:section:load', () => {
    initializeBuyNowModals();
    cleanupCartWishlistArtifacts();
    if (lastCart) {
      renderCartState(lastCart);
      cleanupCartWishlistArtifacts();
      window.setTimeout(cleanupCartWishlistArtifacts, 250);
    }
  });

  let cleanupTimer = null;
  const scheduleWishlistCleanup = () => {
    if (cleanupTimer) {
      window.clearTimeout(cleanupTimer);
    }

    cleanupTimer = window.setTimeout(() => {
      cleanupTimer = null;
      cleanupCartWishlistArtifacts();
    }, 50);
  };

  if (window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes && mutation.addedNodes.length > 0)) {
        scheduleWishlistCleanup();
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.ZoeDIYOOSQ1 = {
    version: VERSION,
    refresh: initialize,
    getCart: () => lastCart,
    save: (option) => saveChoice(option),
    clear: () => saveChoice(null),
    flush: flushMirror
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
